import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { SEOBreadcrumb } from '@/components/SEOBreadcrumb';
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { SEO } from "@/components/SEO";
import { useJobDetail } from "@/hooks/useJobDetail";
import { JobApplicationModal } from "@/components/jobs/JobApplicationModal";
import { BrutalButton } from "@/components/ui/BrutalButton";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { SocialShareDropdown } from "@/components/SocialShareDropdown";
import { supabase } from "@/integrations/supabase/client";
import LoginGate from "@/components/auth/LoginGate";
import { ReportModal } from "@/components/reports/ReportModal";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useAuth } from "@/contexts/AuthContext";
import { sanitizeHtml } from "@/lib/sanitize";
import { 
  Briefcase, MapPin, Building2, ExternalLink, 
  ArrowLeft, Calendar, Flag
} from "lucide-react";
import { format } from "date-fns";
import { it, enUS, es } from "date-fns/locale";
import { contractTypes, workModes, salaryPeriods, inclusivityTags, inclusiveBenefits } from "@/data/jobOptions";

const JobDetail = () => {
  const { slug } = useParams<{ slug: string }>();
  const { t, i18n } = useTranslation();
  const { data: job, isLoading, error } = useJobDetail(slug);
  const { user } = useAuth();
  const [isApplicationOpen, setIsApplicationOpen] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);

  const getLocale = () => {
    switch (i18n.language) {
      case 'en': return enUS;
      case 'es': return es;
      default: return it;
    }
  };

  // Get description in current language with fallback to Italian then legacy field
  const getLocalizedDescription = (): string => {
    if (!job) return '';
    
    // Type assertion to handle new fields that may not be in generated types yet
    const jobWithMultilang = job as typeof job & {
      description_it?: string;
      description_en?: string;
      description_es?: string;
    };
    
    const lang = i18n.language?.split('-')[0] || 'it';
    
    switch (lang) {
      case 'en':
        return jobWithMultilang.description_en || jobWithMultilang.description_it || job.description || '';
      case 'es':
        return jobWithMultilang.description_es || jobWithMultilang.description_it || job.description || '';
      default:
        return jobWithMultilang.description_it || job.description || '';
    }
  };

  useEffect(() => {
    if (job?.id) {
      supabase.functions.invoke('track-job-view', {
        body: { job_posting_id: job.id }
      }).catch(console.error);
    }
  }, [job?.id]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <Header />
        <main className="flex-1 container mx-auto px-4 py-8">
          <div className="max-w-4xl mx-auto">
            <div className="border-[3px] border-dark bg-surface shadow-[8px_8px_0px_0px] shadow-dark p-8">
              <div className="flex items-start gap-6">
                <Skeleton className="w-24 h-24 md:w-32 md:h-32 rounded-full" />
                <div className="flex-1 space-y-4">
                  <Skeleton className="h-10 w-2/3" />
                  <Skeleton className="h-6 w-1/2" />
                  <Skeleton className="h-4 w-1/3" />
                </div>
              </div>
              <div className="mt-8 space-y-4">
                <Skeleton className="h-48 w-full" />
                <Skeleton className="h-32 w-full" />
              </div>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (error || !job) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <Header />
        <main className="flex-1 container mx-auto px-4 py-8">
          <div className="max-w-4xl mx-auto text-center py-16">
            <Briefcase className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
            <h1 className="font-heading text-2xl font-bold mb-4 text-dark">
              {t("jobs.detail.notFound")}
            </h1>
            <Link to="/jobs">
              <BrutalButton>
                <ArrowLeft className="w-4 h-4 mr-2" />
                {t("jobs.backToList")}
              </BrutalButton>
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const contractData = contractTypes.find(c => c.value === job.contract_type);
  const workModeData = workModes.find(w => w.value === job.work_mode);
  const salaryPeriodData = salaryPeriods.find(s => s.value === job.salary_period);

  const formatSalary = () => {
    if (!job.salary_range_min && !job.salary_range_max) return null;
    const min = job.salary_range_min?.toLocaleString('it-IT');
    const max = job.salary_range_max?.toLocaleString('it-IT');
    const periodLabel = salaryPeriodData ? t(salaryPeriodData.labelKey) : '';
    if (min && max) return `€${min} - €${max} ${periodLabel}`;
    if (min) return `${t('jobs.detail.salaryFrom', { amount: min })} ${periodLabel}`;
    if (max) return `${t('jobs.detail.salaryUpTo', { amount: max })} ${periodLabel}`;
    return null;
  };

  const handleApply = () => {
    supabase.functions.invoke('track-job-click', {
      body: { job_posting_id: job.id }
    }).catch(console.error);

    if (job.application_method === 'external_link' && job.application_url) {
      window.open(job.application_url, '_blank');
    } else if (job.application_method === 'email' && job.application_email) {
      window.location.href = `mailto:${job.application_email}?subject=${encodeURIComponent(t('jobs.detail.emailSubject', { title: job.title }))}`;
    } else {
      setIsApplicationOpen(true);
    }
  };

  const contractLabel = contractData ? t(contractData.labelKey) : job.contract_type;
  const workModeLabel = workModeData ? t(workModeData.labelKey) : job.work_mode;

  // Check if job is new (published within last 7 days)
  const sevenDaysAgo = new Date();
  sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
  const isNew = job.published_at && new Date(job.published_at) > sevenDaysAgo;

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <SEO 
        title={`${job.title} | ${job.companies?.name || 'Inclusia'}`}
        description={getLocalizedDescription().replace(/<[^>]*>/g, '').substring(0, 160)}
      />
      <Header />

      <main className="flex-1 container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          {/* Single Container for All Sections */}
          <div className="border-[3px] border-dark bg-surface shadow-[8px_8px_0px_0px] shadow-dark">
            
            {/* Job Header - Linear Layout */}
            <div className="px-6 md:px-8 py-6 md:py-8">
              <div className="mb-4">
                <SEOBreadcrumb 
                  items={[
                    { label: t('common.jobs'), href: '/jobs' },
                    { label: job.title }
                  ]}
                  align="left"
                />
              </div>

              {/* Title with NEW badge and Report Flag */}
              <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
                <div className="flex flex-wrap items-center gap-3">
                  <h1 className="font-heading text-3xl md:text-5xl font-black text-dark">
                    {job.title}
                  </h1>
                  {isNew && (
                    <Badge className="bg-amber-500 text-dark text-[10px] font-bold uppercase px-1.5 py-0.5 border-0 shrink-0">
                      {t('jobs.card.new')}
                    </Badge>
                  )}
                </div>
                {user && (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          onClick={() => setShowReportModal(true)}
                          className="p-2 text-muted-foreground hover:text-destructive transition-colors shrink-0"
                          aria-label={t('reports.reportJob')}
                        >
                          <Flag className="w-4 h-4" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>{t('reports.reportJob')}</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
              </div>

              {/* Date Row */}
              <div className="flex items-center gap-2 text-muted-foreground mb-3">
                <Calendar className="w-4 h-4" />
                <span className="text-sm font-medium">
                  {job.published_at && format(new Date(job.published_at), 'd MMMM yyyy', { locale: getLocale() })}
                </span>
              </div>

              {/* Location Row */}
              <div className="flex items-center gap-2 text-dark mb-3">
                <MapPin className="w-4 h-4 text-muted-foreground" />
                <span className="text-sm font-medium">
                  {job.location_city} • {workModeLabel}
                </span>
              </div>

              {/* Contract + Salary Row */}
              <div className="flex items-center gap-2 text-dark mb-4">
                <Briefcase className="w-4 h-4 text-muted-foreground" />
                <span className="text-sm font-medium">
                  {contractLabel}
                  {formatSalary() && ` • ${formatSalary()}`}
                </span>
              </div>


              {/* Action Buttons */}
              <div className="flex flex-wrap items-center justify-between gap-3">
                <LoginGate 
                  title={t('auth.gate.applyToJob')}
                  description={t('auth.gate.registerFreeToApply')}
                  variant="inline"
                >
                  <BrutalButton onClick={handleApply}>
                    {job.application_method === 'external_link' ? (
                      <>
                        {t("jobs.detail.applyNow")}
                        <ExternalLink className="w-4 h-4 ml-2" />
                      </>
                    ) : (
                      t("jobs.detail.applyNow")
                    )}
                  </BrutalButton>
                </LoginGate>
                
                <SocialShareDropdown 
                  url={window.location.href}
                  title={job.title}
                  buttonText={t("common.share", "Condividi")}
                />
              </div>
            </div>

            <Separator className="h-[3px] bg-dark" />

            {/* Description Section */}
            <div className="px-6 md:px-8 py-4 md:py-6">
              <h2 className="font-heading text-xl font-bold text-dark mb-4">
                {t("jobs.detail.detailsFor", { title: job.title })}
              </h2>
              <div 
                className="prose prose-sm max-w-none text-dark"
                dangerouslySetInnerHTML={{ 
                  __html: sanitizeHtml(getLocalizedDescription(), {
                    allowedTags: ['p', 'br', 'strong', 'em', 'b', 'i', 'ul', 'ol', 'li', 'h1', 'h2', 'h3', 'blockquote']
                  }) 
                }}
              />
            </div>

            {/* Skills Section */}
            {(job.required_skills?.length > 0 || job.nice_to_have_skills?.length > 0) && (
              <div className="px-6 md:px-8 pt-0 pb-4 md:pb-6">
                <h2 className="font-heading text-xl font-bold text-dark mb-4">
                  {t("jobs.detail.skillsFor", { title: job.title })}
                </h2>
                {job.required_skills && job.required_skills.length > 0 && (
                  <div className="mb-4">
                    <h3 className="font-heading text-sm font-bold uppercase text-muted-foreground tracking-wider mb-3">
                      {t("jobs.detail.requiredSkills")}
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {job.required_skills.map((skill, i) => (
                        <span 
                          key={i} 
                          className="px-3 py-1 bg-primary/10 border border-dark/20 text-sm font-medium"
                        >
                          {skill}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {job.nice_to_have_skills && job.nice_to_have_skills.length > 0 && (
                  <div>
                    <h3 className="font-heading text-sm font-bold uppercase text-muted-foreground tracking-wider mb-3">
                      {t("jobs.detail.niceToHave")}
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {job.nice_to_have_skills.map((skill, i) => (
                        <span 
                          key={i} 
                          className="px-3 py-1 bg-accent/10 border border-dark/20 text-sm font-medium"
                        >
                          {skill}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Benefits Section */}
            {job.inclusive_benefits && job.inclusive_benefits.length > 0 && (
              <div className="px-6 md:px-8 pt-0 pb-4 md:pb-6">
                <h2 className="font-heading text-xl font-bold text-dark mb-4">
                  {t("jobs.detail.benefits")}
                </h2>
                <div className="flex flex-wrap gap-2">
                  {job.inclusive_benefits.map((benefit, i) => {
                    const benefitData = inclusiveBenefits.find(b => b.value === benefit);
                    return (
                      <span 
                        key={i} 
                        className="px-3 py-1 bg-primary/10 border border-dark/20 text-sm font-medium"
                      >
                        {benefitData ? t(benefitData.labelKey) : benefit}
                      </span>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Company Box - Like Sede in CompanyProfile */}
            {job.companies && (
              <>
                <Separator className="h-[3px] bg-dark" />
                <div className="px-6 md:px-8 py-4 md:py-6">
                  <h3 className="font-heading text-sm font-bold uppercase text-muted-foreground tracking-wider mb-4">
                    {t("jobs.detail.aboutCompany")}
                  </h3>
                  <div className="border-[3px] border-dark bg-card shadow-[4px_4px_0px_0px] shadow-dark p-4 md:p-6">
                    <div className="flex items-center gap-4">
                      {job.companies.logo_url ? (
                        <img 
                          src={job.companies.logo_url} 
                          alt={job.companies.name}
                          className="w-16 h-16 rounded-full border-[3px] border-dark object-cover bg-white"
                        />
                      ) : (
                        <div className="w-16 h-16 rounded-full border-[3px] border-dark bg-muted flex items-center justify-center">
                          <Building2 className="w-8 h-8 text-muted-foreground" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="font-heading text-lg font-bold text-dark truncate">{job.companies.name}</p>
                        {(job.companies as any).address_city && (
                          <p className="text-sm text-muted-foreground flex items-center gap-1">
                            <MapPin className="w-3 h-3" />
                            {(job.companies as any).address_city}
                          </p>
                        )}
                      </div>
                      <Link to={`/company/${job.companies.slug}`}>
                        <BrutalButton variant="secondary" size="sm">
                          {t("jobs.detail.viewCompany")}
                        </BrutalButton>
                      </Link>
                    </div>

                    {/* Work Environment - Inclusivity Tags */}
                    {job.inclusivity_tags && job.inclusivity_tags.length > 0 && (
                      <div className="mt-6 pt-4 border-t border-dark/20">
                        <div className="flex flex-wrap items-center gap-3">
                          <h4 className="font-heading text-sm font-bold uppercase text-muted-foreground tracking-wider shrink-0">
                            {t("jobs.detail.workEnvironment")}
                          </h4>
                          {job.inclusivity_tags.map((tag) => {
                            const tagInfo = inclusivityTags.find(t => t.value === tag);
                            return (
                              <Badge 
                                key={tag} 
                                className="bg-purple-500 text-white text-[10px] px-2 py-0.5 border-0 font-bold uppercase"
                              >
                                {tagInfo?.icon} {tagInfo ? t(tagInfo.labelKey) : tag}
                              </Badge>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </main>

      <Footer />

      <JobApplicationModal
        open={isApplicationOpen}
        onClose={() => setIsApplicationOpen(false)}
        job={job}
      />

      <ReportModal
        isOpen={showReportModal}
        onClose={() => setShowReportModal(false)}
        jobId={job?.id}
        targetName={job?.title || ''}
      />
    </div>
  );
};

export default JobDetail;
